import React, {
  useEffect,
  useMemo,
  useState,
  useDeferredValue,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { useServices } from "../../context/ServiceProvider";
import { pushBreadcrumb } from "../../store/slices/navigationSlice";
import { Alert, Spin, Tabs, Typography, Button, Popover, Select } from "antd";
import PreviewOrchestrator from "../preview/PreviewOrchestrator";
import { useRecents } from "../../hooks/useRecents";
import {
  renderGrid,
  setByPathCloned,
  buildMinimalPatch,
  getByPath,
  formatFileFieldDisplay,
} from "../common/FieldRenderer";
import {
  EyeOff,
  Eye,
  Edit,
  Info,
  FileText,
  Settings,
  Zap,
  HelpCircle,
  Save,
} from "lucide-react";
import { validateProjectPageComponentEditForm } from "../../config/projectPageComponentEdit.validation";
import {
  PPC_PICKLIST_LABELS,
  buildInfoFieldsForPpc,
  buildContentFieldsForPpc,
  buildSysFieldsForPpc,
  PPC_MEDIA_UPLOAD_FIELDS,
} from "./projectPageComponentColumnsConfig";
import ImageLocationFieldInline from "./ImageLocationFieldInline";
import {
  getPresignedUploadUrl,
  uploadFileToPresignedUrl,
  deleteS3Object,
} from "../../services/backend/s3Api";
import { usePicklistMap, resolvePicklistFields } from "../../hooks/usePicklist";
import { SOBJECT_PROJECT_PAGE_COMPONENT } from "../../config/sobjectNames";

/** True if value is a string that looks like a valid image URL for preview (img src). */
function isValidImageUrl(v) {
  return (
    typeof v === "string" &&
    (v.startsWith("http://") || v.startsWith("https://"))
  );
}

export const PPC_TYPE_TO_ESM = {
  "2 Column 50-50": "TwoColumn5050",
  "3 Column 33-33-33": "ThreeColumn33",
  "2 Column Image Left 50-50": "TwoColumnImageLeft5050",
  "2 Column Image Right 50-50": "TwoColumnImageRight5050",
  "2 Column Image Left 33-66": "TwoColumnImageLeft3366",
  "2 Column Image Right 33-66": "TwoColumnImageRight3366",
  "Headline only": "HeadlineOnly",
  "Text Only Full Page": "TextOnlyFullPage",
  "Standalone Button": "StandaloneButton",
  "Image Only": "ImageOnly",
  "Microsoft Project Page": "ClientTasks",
  "Splash Page": "SplashPage",
  ClientTasks: "ClientTasks",
  ClientTasksFullView: "ClientTasksFullView",
  ProjectCard: "ProjectCard",
  ProjectCardInvite: "ProjectCardInvite",
  Banner: "Banner",
};

const SUB_TYPES = {
  SplashPage: [
    {
      label: "Project Card",
      value: "ProjectCard",
    },
    {
      label: "Project Card - Invite",
      value: "ProjectCardInvite",
    },
    {
      label: "Banner",
      value: "Banner",
    },
  ],
  ClientTasks: [
    {
      label: "Client Tasks",
      value: "ClientTasks",
    },
    {
      label: "Client Tasks Full View",
      value: "ClientTasksFullView",
    },
  ],
};

function mapPpcToProps(esmName, data) {
  const content = data?.content || {};

  const baseProps = {};
  switch (esmName) {
    case "HeadlineOnly":
      baseProps.title = content.headline1 || "";
      break;
    case "TextOnlyFullPage":
      baseProps.content = content.textArea1 || "";
      break;
    case "StandaloneButton":
      baseProps.buttonName = content.buttonName1 || "";
      baseProps.ctaUrl = content.buttonUrl1 || content.fileUrl1 || "";
      baseProps.onButtonClick = (url) => {
        if (content.buttonType1 === "Direct URL") {
          window.open(url, "_blank");
        } else if (content.buttonType1 === "File Download") {
          window.open(url, "_blank");
        } else if (content.buttonType1 === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "ImageOnly":
      baseProps.mediaUrl = content.imageLocation1 || "";
      break;
    case "TwoColumn5050":
      baseProps.leftTitle = content.headline1 || "";
      baseProps.leftContent = content.textArea1 || "";
      baseProps.leftButtonName = content.buttonName1 || undefined;
      baseProps.leftCtaUrl =
        content.buttonUrl1 || content.fileUrl1 || undefined;
      baseProps.leftMediaUrl = content.imageLocation1 || undefined;
      baseProps.leftButtonType = content.buttonType1 || undefined;
      baseProps.rightTitle = content.headline2 || "";
      baseProps.rightContent = content.textArea2 || "";
      baseProps.rightButtonName = content.buttonName2 || undefined;
      baseProps.rightCtaUrl =
        content.buttonUrl2 || content.fileUrl2 || undefined;
      baseProps.rightMediaUrl = content.imageLocation2 || undefined;
      baseProps.rightButtonType = content.buttonType2 || undefined;
      baseProps.onButtonClick = (url) => {
        if (content.buttonType2 === "Direct URL") {
          window.open(url, "_blank");
        } else if (content.buttonType2 === "File Download") {
          window.open(url, "_blank");
        } else if (content.buttonType2 === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "ThreeColumn33":
      baseProps.leftTitle = content.headline1 || "";
      baseProps.leftContent = content.textArea1 || "";
      baseProps.leftButtonName = content.buttonName1 || undefined;
      baseProps.leftCtaUrl =
        content.buttonUrl1 || content.fileUrl1 || undefined;
      baseProps.leftMediaUrl = content.imageLocation1 || undefined;
      baseProps.leftButtonType = content.buttonType1 || undefined;
      baseProps.middleTitle = content.headline2 || "";
      baseProps.middleContent = content.textArea2 || "";
      baseProps.middleButtonName = content.buttonName2 || undefined;
      baseProps.middleCtaUrl =
        content.buttonUrl2 || content.fileUrl2 || undefined;
      baseProps.middleMediaUrl = content.imageLocation2 || undefined;
      baseProps.middleButtonType = content.buttonType2 || undefined;
      baseProps.rightTitle = content.headline3 || "";
      baseProps.rightContent = content.textArea3 || "";
      baseProps.rightButtonName = content.buttonName3 || undefined;
      baseProps.rightCtaUrl =
        content.buttonUrl3 || content.fileUrl3 || undefined;
      baseProps.rightMediaUrl = content.imageLocation3 || undefined;
      baseProps.rightButtonType = content.buttonType3 || undefined;
      baseProps.onButtonClick = (url) => {
        if (content.buttonType3 === "Direct URL") {
          window.open(url, "_blank");
        } else if (content.buttonType3 === "File Download") {
          window.open(url, "_blank");
        } else if (content.buttonType3 === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "TwoColumnImageLeft3366":
      baseProps.title = content.headline2 || "";
      baseProps.content = content.textArea2 || "";
      baseProps.buttonName = content.buttonName2 || undefined;
      baseProps.ctaUrl = content.buttonUrl2 || content.fileUrl2 || undefined;
      baseProps.mediaUrl = content.imageLocation1 || undefined;
      baseProps.buttonType = content.buttonType2 || undefined;
      baseProps.onButtonClick = (url) => {
        if (content.buttonType2 === "Direct URL") {
          window.open(url, "_blank");
        } else if (content.buttonType2 === "File Download") {
          window.open(url, "_blank");
        } else if (content.buttonType2 === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "TwoColumnImageRight3366":
      baseProps.title = content.headline1 || "";
      baseProps.content = content.textArea1 || "";
      baseProps.buttonName = content.buttonName1 || undefined;
      baseProps.ctaUrl = content.buttonUrl1 || content.fileUrl1 || undefined;
      baseProps.mediaUrl = content.imageLocation2 || undefined;
      baseProps.buttonType = content.buttonType1 || undefined;
      baseProps.onButtonClick = (url) => {
        if (content.buttonType1 === "Direct URL") {
          window.open(url, "_blank");
        } else if (content.buttonType1 === "File Download") {
          window.open(url, "_blank");
        } else if (content.buttonType1 === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "TwoColumnImageLeft5050":
      baseProps.title = content.headline2 || "";
      baseProps.content = content.textArea2 || "";
      baseProps.buttonName = content.buttonName2 || undefined;
      baseProps.ctaUrl = content.buttonUrl2 || content.fileUrl2 || undefined;
      baseProps.mediaUrl = content.imageLocation1 || undefined;
      baseProps.buttonType = content.buttonType2 || undefined;
      baseProps.onButtonClick = (url) => {
        if (content.buttonType2 === "Direct URL") {
          window.open(url, "_blank");
        } else if (content.buttonType2 === "File Download") {
          window.open(url, "_blank");
        } else if (content.buttonType2 === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "TwoColumnImageRight5050":
      baseProps.title = content.headline1 || "";
      baseProps.content = content.textArea1 || "";
      baseProps.buttonName = content.buttonName1 || undefined;
      baseProps.ctaUrl = content.buttonUrl1 || content.fileUrl1 || undefined;
      baseProps.mediaUrl = content.imageLocation2 || undefined;
      baseProps.buttonType = content.buttonType1 || undefined;
      baseProps.onButtonClick = (url) => {
        if (content.buttonType1 === "Direct URL") {
          window.open(url, "_blank");
        } else if (content.buttonType1 === "File Download") {
          window.open(url, "_blank");
        } else if (content.buttonType1 === "Qualification URL") {
          // TODO: Implement Qualification URL
          window.open(url, "_blank");
        }
      };
      break;
    case "ProjectCard":
      const project = {
        projectObjectiveName: content.headline1 || "",
        projectDescription: content.textArea1 || "",
        imageUrl: content.imageLocation1 || "",
        payRate: content.buttonUrl1 || "",
        button: content.buttonType1 || "",
        workUrl: content.buttonUrl1 || "",
        unitType: "Hour",
        projectContributorStatus: "",
      };
      baseProps.project = project;
      break;
    case "ProjectCardInvite":
      const projectInvite = {
        projectObjectiveName: content.headline1 || "",
        projectDescription: content.textArea1 || "",
        imageUrl: content.imageLocation1 || "",
        payRate: content.buttonUrl1 || "",
        button: content.buttonType1 || "",
        workUrl: content.buttonUrl1 || "",
        unitType: "Hour",
        invitedToProjectDate: new Date() || "",
        projectContributorStatus: "Invite",
      };
      baseProps.project = projectInvite;
      baseProps.esmName = "ProjectCard";
      break;
    case "Banner":
      const activeMatchedProjects = [];
      const project1 = {
        type_name_match: "Project Match #1",
        type_name_active: "Project Active #1",
        type: "match",
        country: "United States",
        projectType: "Project Active #1",
        headline2: content.headline2 || "",
        textArea2: content.textArea2 || "",
        projectObjectiveName: content.headline1 || "",
        projectDescription: content.textArea1 || "",
        imageUrl: content.imageLocation1 || "",
        payRate: content.buttonUrl1 || "",
        button: content.buttonType1 || "",
        workUrl: content.buttonUrl1 || "",
        unitType: "Hour",
      };
      activeMatchedProjects.push(project1);
      baseProps.carouselWidth = "100%";
      baseProps.setCurrentSlide = 1;
      baseProps.handleCarouselViewAll = () => {};
      baseProps.activeMatchedProjects = activeMatchedProjects;
      break;
    case "ClientTasks":
      baseProps.projectStatus = "Active";
      baseProps.projectPage = content;
      baseProps.onButtonClick = (url) => {
        if (content.buttonType1 === "Direct URL") {
          window.open(url, "_blank");
        } else if (content.buttonType1 === "File Download") {
          window.open(url, "_blank");
        }
      };
      baseProps.loading = false;
      baseProps.isError = false;
      const data = [
        {
          name: "Task 1",
          description: "Task 1 description",
          numAvailableHits: 10,
          judgingUrl: "https://www.google.com",
        },
        {
          name: "Task 2",
          description: "Task 2 description",
          numAvailableHits: 0,
          judgingUrl: "https://www.google.com",
        },
        {
          name: "Task 3",
          description: "Task 3 description",
          numAvailableHits: 1,
          judgingUrl: "https://www.google.com",
        },
      ];
      baseProps.groupedSlides = [[...data], [...data], [...data]];
      baseProps.ALLOWED_COUNTRIES = ["All"];
      baseProps.data = data;
      baseProps.tasksData = data;
      break;
    case "ClientTasksFullView":
      baseProps.projectStatus = "Active";
      baseProps.projectPage = content;
      baseProps.isViewAll = true;
      baseProps.onButtonClick = (url) => {
        if (content.buttonType1 === "Direct URL") {
          window.open(url, "_blank");
        } else if (content.buttonType1 === "File Download") {
          window.open(url, "_blank");
        }
      };
      baseProps.loading = false;
      baseProps.isError = false;
      const tasks = [
        {
          name: "Task 1",
          description: "Task 1 description",
          numAvailableHits: 10,
          judgingUrl: "https://www.google.com",
        },
        {
          name: "Task 2",
          description: "Task 2 description",
          numAvailableHits: 0,
          judgingUrl: "https://www.google.com",
        },
        {
          name: "Task 3",
          description: "Task 3 description",
          numAvailableHits: 1,
          judgingUrl: "https://www.google.com",
        },
      ];
      baseProps.ALLOWED_COUNTRIES = ["All"];
      baseProps.tasksData = tasks;
      break;
    case "ProjectQualificationPage":
      baseProps.qualSteps = {
        data: {
          data: {
            status: "Pending",
            steps: [
              {
                id: "1",
                name: "Qualification Step 1",
                status: "Pending",
                funnel: "Funnel 1",
                attempts: 0,
                qualificationStep: "Qualification Step 1",
              },
            ],
          },
        },
      };
      baseProps.esmName = "ProjectQualificationPage";
      baseProps.props = baseProps.qualSteps;
      break;
    default:
      console.log("Unknown Map Ppc to ESM: ", esmName);
      break;
  }
  return baseProps;
}

export default function ProjectPageComponentDetails() {
  const { projectPageComponent, http } = useServices();
  const { projectPageComponentId } = useParams();
  const dispatch = useDispatch();
  const breadcrumbs = useSelector((state) => state.navigation.breadcrumbs);
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(null);

  const { picklistMap } = usePicklistMap(
    Object.values(PPC_PICKLIST_LABELS),
    SOBJECT_PROJECT_PAGE_COMPONENT,
  );
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [hideValidationSuccess, setHideValidationSuccess] = useState(false);
  const [headerWidth, setHeaderWidth] = useState("auto");
  const [previewWidth, setPreviewWidth] = useState(50); // Percentage of available width
  const [isDragging, setIsDragging] = useState(false);
  const [ppcType, setPpcType] = useState(null);
  const [subTypes, setSubTypes] = useState(false);
  const [payload, setPayload] = useState(null);
  const [presignedUrls, setPresignedUrls] = useState({
    imageLocation1: "",
    imageLocation2: "",
    imageLocation3: "",
  });
  // Pending files for image fields: upload to S3 only on Save; preview uses object URL
  const [pendingImageFiles, setPendingImageFiles] = useState({
    "content.imageLocation1": null,
    "content.imageLocation2": null,
    "content.imageLocation3": null,
  });
  const objectUrlRefs = useRef({});

  // Revoke object URLs when pending files change or unmount
  useEffect(() => {
    const refs = objectUrlRefs.current;
    return () => {
      Object.values(refs).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      objectUrlRefs.current = {};
    };
  }, []);

  const setPendingImageFile = (path, file) => {
    if (objectUrlRefs.current[path]) {
      URL.revokeObjectURL(objectUrlRefs.current[path]);
      objectUrlRefs.current[path] = null;
    }
    if (file) objectUrlRefs.current[path] = URL.createObjectURL(file);
    setPendingImageFiles((s) => ({ ...s, [path]: file }));
  };

  const handleImageDelete = (path) => {
    const value = getByPath(form || data, path);
    if (value && typeof value === "string" && !value.startsWith("http")) {
      deleteS3Object(value.trim()).catch(() => {});
    }
    handleChange(path, "");
    setPendingImageFiles((s) => ({ ...s, [path]: null }));
  };

  const handleImageRemovePath = (path) => {
    handleChange(path, "");
    setPendingImageFiles((s) => ({ ...s, [path]: null }));
  };

  const { record } = useRecents({ storageKey: "RECENTS::v1", maxItems: 50 });
  const recordedIdRef = React.useRef(null);
  const headerRef = useRef(null);
  const containerRef = useRef(null);

  // Handle scrollable header when content overflows
  useEffect(() => {
    const checkOverflow = () => {
      if (headerRef.current) {
        const headerElement = headerRef.current;
        const innerContent = headerElement.querySelector(
          ".flex.items-center.justify-between",
        );

        if (innerContent) {
          // Get header position from top
          const headerPosition = headerElement.getBoundingClientRect().top;
          setHeaderWidth(headerPosition <= 0 ? "100%" : "auto");
        }
      }
    };

    // Check on mount and resize
    const timeoutId = setTimeout(checkOverflow, 100);
    window.addEventListener("resize", checkOverflow);

    // Also check when data changes (buttons might change)
    if (data) {
      setTimeout(checkOverflow, 100);
    }

    // Use ResizeObserver for more accurate detection
    let resizeObserver;
    if (headerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        setTimeout(checkOverflow, 50);
      });
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkOverflow);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [data, isEditing, showPreview]);

  // Handle preview resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left - 20;
      const newWidth = Math.min(
        Math.max((mouseX / containerWidth) * 100, 20),
        66.67,
      ); // Max 2/3 (66.67%)

      setPreviewWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  // Use image URLs from Apex only (GET returns presigned). No Node presigned-download; when value is S3 key, no preview until refetch.
  const contentForKeys = form?.content ?? data?.content ?? {};
  useEffect(() => {
    const v1 = contentForKeys.imageLocation1;
    const v2 = contentForKeys.imageLocation2;
    const v3 = contentForKeys.imageLocation3;
    setPresignedUrls({
      imageLocation1: isValidImageUrl(v1) ? v1 : "",
      imageLocation2: isValidImageUrl(v2) ? v2 : "",
      imageLocation3: isValidImageUrl(v3) ? v3 : "",
    });
  }, [
    contentForKeys.imageLocation1,
    contentForKeys.imageLocation2,
    contentForKeys.imageLocation3,
  ]);

  // Update PPC Type when record type changes (payload is updated by the content/presigned effect)
  useEffect(() => {
    updatePreviewType(form || data);
  }, [data?.info?.recordTypeName, form?.info?.recordTypeName]);

  // Update Preview Props: use pending file object URL when selected (preview before upload), else presigned or direct URL
  useEffect(() => {
    if (!ppcType) return;
    const dataOrForm = form || data;
    if (!dataOrForm) return;
    const content = dataOrForm.content || {};
    const pendingUrl = (path) =>
      pendingImageFiles[path] && objectUrlRefs.current[path]
        ? objectUrlRefs.current[path]
        : "";
    const resolve = (slot, path) => {
      const pending = pendingUrl(path);
      if (pending) return pending;
      const presigned = presignedUrls[slot];
      if (isValidImageUrl(presigned)) return presigned;
      if (isValidImageUrl(content[slot])) return content[slot];
      return "";
    };
    const contentResolved = {
      ...content,
      imageLocation1: resolve("imageLocation1", "content.imageLocation1"),
      imageLocation2: resolve("imageLocation2", "content.imageLocation2"),
      imageLocation3: resolve("imageLocation3", "content.imageLocation3"),
    };
    const dataResolved = { ...dataOrForm, content: contentResolved };
    const props = mapPpcToProps(ppcType, dataResolved);
    setPayload(props);
  }, [ppcType, data, form, presignedUrls, pendingImageFiles]);

  const updatePreviewType = (dataOrForm) => {
    console.log("Update Preview Type entered");
    if (!dataOrForm) return;
    let esmName = PPC_TYPE_TO_ESM[dataOrForm?.info?.recordTypeName] || "";

    if (esmName === "SplashPage" || esmName === "ClientTasks") {
      setSubTypes(SUB_TYPES[esmName]);
      esmName = SUB_TYPES[esmName][0].value;
    } else if (subTypes) {
      setSubTypes(null);
    }

    // Display sub preview types if applicable
    console.log("Update Preview Type", esmName);
    setPpcType(esmName);
    return esmName;
  };

  const updatePreviewProps = (esmName, dataOrForm) => {
    console.log("Update Preview Props entered");
    if (!dataOrForm) return;
    const props = mapPpcToProps(esmName, dataOrForm);
    console.log("Update Preview Props", esmName, props);
    setPayload(props);
  };

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!projectPageComponentId) return;
      setData(null);
      setLoading(true);
      setError("");
      try {
        const record = await projectPageComponent.fetchProjectPageComponent(
          projectPageComponentId,
        );
        if (!ignore) setData(record);
      } catch (e) {
        if (!ignore)
          setError(e?.message || "Failed to load project page component");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [projectPageComponentId, projectPageComponent]);

  // Push breadcrumb when PPC loads
  useEffect(() => {
    const id = data?.id || projectPageComponentId;
    const provided = location?.state?.crumbLabel;
    const fallbackName = data?.info?.name;
    const name = provided || fallbackName;
    if (id && name) {
      if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
        dispatch(pushBreadcrumb({ label: "Home", path: "/", type: "home" }));
      }
      dispatch(
        pushBreadcrumb({
          id,
          label: name || `Component ${id}`,
          path: `/ppc/${id}`,
          type: "projectPageComponent",
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.id,
    data?.info?.name,
    projectPageComponentId,
    location.pathname,
    breadcrumbs?.length,
    location?.state?.crumbLabel,
  ]);

  // Record in Recently Viewed when opened
  useEffect(() => {
    const id = data?.id || projectPageComponentId;
    if (data == null || !id || recordedIdRef.current === id) return;
    const name = data?.info?.name || "Component";
    record({
      id: String(id),
      title: String(name || id),
      url: `/ppc/${id}`,
      type: "Project Page Component",
    });
    recordedIdRef.current = id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, projectPageComponentId]);

  const sys = data?.sys || {};

  useEffect(() => {
    if (isEditing) {
      setForm(data);
      setFormErrors({});
      setSuccess("");
    }
  }, [isEditing, data]);
  // Use a deferred version of the preview record to keep typing smooth
  const previewRecord = useMemo(
    () => (isEditing ? form || data : data),
    [isEditing, form, data],
  );
  const deferredPreviewRecord = useDeferredValue(previewRecord);

  const validationGroups = useMemo(() => {
    const alerts = Array.isArray(validation?.alerts) ? validation.alerts : [];
    const groups = { error: [], warning: [], info: [] };
    for (const a of alerts) {
      const t = (a?.type === "warn" ? "warning" : a?.type) || "info";
      if (t === "error" || t === "warning" || t === "info") {
        groups[t].push(a);
      } else {
        groups.info.push(a);
      }
    }
    return groups;
  }, [validation]);

  function handleChange(path, value) {
    const next = setByPathCloned(form || {}, path, value);
    setForm(next);
    setFormErrors(validateProjectPageComponentEditForm(next));
    setError("");
  }

  async function handleSave() {
    if (!form) return;
    const errs = validateProjectPageComponentEditForm(form);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError("");
      return;
    }
    const id = data?.id || projectPageComponentId;
    const projectPageId = (form || data)?.info?.projectPageId;
    if (!id) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      let nextForm = { ...form };
      const pathToField = {
        "content.imageLocation1": "Image_Location_1__c",
        "content.imageLocation2": "Image_Location_2__c",
        "content.imageLocation3": "Image_Location_3__c",
      };
      const hasPendingUploads = Object.values(pendingImageFiles).some(
        (f) => f && f instanceof File,
      );
      for (const path of Object.keys(pendingImageFiles)) {
        const file = pendingImageFiles[path];
        if (!file || !(file instanceof File)) continue;
        const apiName = pathToField[path];
        if (!apiName || !projectPageId) continue;
        const key = `${projectPageId}/${id}/${apiName}/${file.name}`;
        const { url } = await getPresignedUploadUrl({
          key,
          contentType: file.type || undefined,
          expiresIn: 900,
        });
        await uploadFileToPresignedUrl(url, file);
        const contentKey = path.replace("content.", "");
        nextForm = setByPathCloned(nextForm, path, key);
        setPendingImageFiles((s) => ({ ...s, [path]: null }));
        if (objectUrlRefs.current[path]) {
          URL.revokeObjectURL(objectUrlRefs.current[path]);
          objectUrlRefs.current[path] = null;
        }
      }
      setForm(nextForm);
      const partial = buildMinimalPatch(data || {}, nextForm) || {};
      if (Object.keys(partial).length === 0) {
        setIsEditing(false);
        setSuccess("No changes to update");
        return;
      }
      await projectPageComponent.patchProjectPageComponent(String(id), partial);
      const fresh = await projectPageComponent.fetchProjectPageComponent(
        String(id),
      );
      setData(fresh);
      setForm(fresh);
      setIsEditing(false);
      setSuccess("Component updated");
    } catch (e) {
      setError(e?.message || "Failed to update component");
    } finally {
      setSaving(false);
    }
  }

  async function handleValidate() {
    const id = data?.id || projectPageComponentId;
    if (!id) {
      setValidationError("Missing record id");
      return;
    }
    setValidationError("");
    setValidating(true);
    setSuccess("");
    setHideValidationSuccess(false);
    try {
      const resp = await http.sfGet(`/Validate`, {
        id: String(id),
        type: "ProjectPageComponent",
      });
      // When a session error occurs, wrapped http methods return null and show a modal
      if (resp == null) {
        setValidation(null);
        return;
      }
      const successFlag = resp?.success === true;
      const alerts = Array.isArray(resp?.alerts) ? resp.alerts : [];
      setValidation({ success: successFlag, alerts });
    } catch (e) {
      const details = e?.details;
      if (details && typeof details === "object") {
        const alerts = Array.isArray(details.alerts) ? details.alerts : [];
        setValidation({ success: !!details.success, alerts });
      } else {
        setValidation(null);
        setValidationError(e?.message || "Validation failed");
      }
    } finally {
      setValidating(false);
    }
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Sticky Header with Title and Action Buttons */}
        <div
          ref={headerRef}
          className={`sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200 rounded-lg p-4 mb-6 shadow-lg transition-all duration-300
          } ${isEditing ? "border-blue-200 bg-blue-50/95" : ""}`}
          style={{ width: headerWidth }}
        >
          <div className={`flex items-center justify-between`}>
            <div className="flex items-center space-x-4">
              <div>
                <div className="flex items-center space-x-2">
                  <Typography.Title
                    level={5}
                    style={{ margin: 0, color: "#6b7280" }}
                  >
                    Project Page Component
                  </Typography.Title>
                  {isEditing && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Edit size={12} className="mr-1" />
                      Editing
                    </span>
                  )}
                </div>
                <Typography.Title level={2} style={{ margin: 0, marginTop: 4 }}>
                  {data?.info?.name || "Unnamed Component"}
                </Typography.Title>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!isEditing ? (
                <Button
                  type="primary"
                  onClick={() => setIsEditing(true)}
                  icon={<Edit size={16} />}
                  className="shadow-sm"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => {
                      setIsEditing(false);
                      setForm(null);
                    }}
                    className="shadow-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSave}
                    disabled={saving || Object.keys(formErrors).length > 0}
                    loading={saving}
                    className="shadow-sm"
                    icon={<Save size={16} />}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              )}

              <div className="h-6 w-px bg-gray-300"></div>

              <Button
                type="default"
                onClick={handleValidate}
                loading={validating}
                className="shadow-sm"
              >
                {validating ? "Validating..." : "Validate"}
              </Button>

              <Button
                type={showPreview ? "primary" : "default"}
                onClick={() => setShowPreview((v) => !v)}
                icon={showPreview ? <EyeOff size={16} /> : <Eye size={16} />}
                className="shadow-sm"
              >
                {showPreview ? "Hide Preview" : "Show Preview"}
              </Button>
            </div>
          </div>
        </div>

        {loading && (
          <div className="mb-6">
            <Spin fullscreen tip="Loading component…" />
          </div>
        )}
        {error && (
          <div className="mb-6">
            <Alert type="error" showIcon message={error} />
          </div>
        )}
        {success && (
          <div className="mb-6">
            <Alert type="success" showIcon message={success} />
          </div>
        )}

        {validationError && (
          <div className="mb-6">
            <Alert type="error" showIcon message={validationError} />
          </div>
        )}

        {validation && (
          <div className="mb-6 space-y-4">
            {Array.isArray(validation.alerts) &&
            validation.alerts.length === 0 ? (
              !hideValidationSuccess ? (
                <div
                  className="fade-out-after-3s"
                  onAnimationEnd={() => setHideValidationSuccess(true)}
                >
                  <Alert
                    type="success"
                    showIcon
                    message="Validation Successful"
                    className="border-green-200 bg-green-50"
                  />
                </div>
              ) : null
            ) : (
              <div className="space-y-4">
                {validationGroups.error.length > 0 && (
                  <Alert
                    type="error"
                    showIcon
                    message={`Validation Errors (${validationGroups.error.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.error.map((a, idx) => (
                          <li key={`err-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-red-200 bg-red-50"
                  />
                )}
                {validationGroups.warning.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message={`Warnings (${validationGroups.warning.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.warning.map((a, idx) => (
                          <li key={`warn-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-yellow-200 bg-yellow-50"
                  />
                )}
                {validationGroups.info.length > 0 && (
                  <Alert
                    type="info"
                    showIcon
                    message={`Information (${validationGroups.info.length})`}
                    description={
                      <ul className="list-disc pl-5 space-y-2 mt-2">
                        {validationGroups.info.map((a, idx) => (
                          <li key={`info-${a.id || idx}`} className="text-sm">
                            {a.message}
                          </li>
                        ))}
                      </ul>
                    }
                    className="border-blue-200 bg-blue-50"
                  />
                )}
              </div>
            )}
          </div>
        )}

        {data && (
          <div
            ref={containerRef}
            className={`${showPreview ? "flex gap-4" : "max-w-6xl mx-auto"} ${
              isDragging ? "select-none cursor-col-resize" : ""
            }`}
          >
            <div
              className={`${
                showPreview ? `flex-shrink-0` : ""
              } h-min bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/20`}
              style={{
                width: showPreview ? `${previewWidth}%` : "100%",
              }}
            >
              <div className="p-6">
                <Tabs
                  defaultActiveKey="info"
                  size="large"
                  tabBarStyle={{
                    marginBottom: 24,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                  items={[
                    {
                      key: "info",
                      label: (
                        <span className="flex items-center gap-2">
                          <Info size={16} />
                          Basic Info
                        </span>
                      ),
                      children: renderGrid(
                        resolvePicklistFields(
                          [
                            {
                              label: "Salesforce Id",
                              value: data?.id || "—",
                              readOnly: true,
                            },
                            ...buildInfoFieldsForPpc({
                              isEditing,
                              data,
                              form,
                            }),
                          ],
                          picklistMap,
                        ),
                        {
                          editing: isEditing,
                          form: form || data,
                          onChange: handleChange,
                          errors: formErrors,
                          onEnableEdit: () => setIsEditing(true),
                          originalData: data,
                        },
                      ),
                    },
                    {
                      key: "content",
                      label: (
                        <span className="flex items-center gap-2">
                          <FileText size={16} />
                          Content
                        </span>
                      ),
                      children: (
                        <>
                          {(() => {
                            const allContentFields = resolvePicklistFields(
                              buildContentFieldsForPpc({
                                isEditing,
                                data,
                                form,
                              }),
                              picklistMap,
                            );

                            const imagePaths = new Set([
                              "content.imageLocation1",
                              "content.imageLocation2",
                              "content.imageLocation3",
                            ]);

                            const gridFields = allContentFields.filter(
                              (f) => !imagePaths.has(f.path),
                            );

                            const allFieldPaths = new Set();
                            allContentFields.forEach((f) => {
                              if (f.path) allFieldPaths.add(f.path);
                              if (f.fields)
                                f.fields.forEach((sf) => {
                                  if (sf.path) allFieldPaths.add(sf.path);
                                });
                            });

                            const visibleMediaFields =
                              PPC_MEDIA_UPLOAD_FIELDS.filter((mf) =>
                                allFieldPaths.has(mf.path),
                              );

                            return (
                              <>
                                {renderGrid(gridFields, {
                                  editing: isEditing,
                                  form: form || data,
                                  onChange: handleChange,
                                  errors: formErrors,
                                  onEnableEdit: () => setIsEditing(true),
                                  originalData: data,
                                })}
                                {visibleMediaFields.length > 0 && isEditing && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {visibleMediaFields.map((f) => (
                                      <ImageLocationFieldInline
                                        key={f.path}
                                        label={f.label}
                                        path={f.path}
                                        value={getByPath(form || data, f.path)}
                                        onChange={handleChange}
                                        onPendingFile={setPendingImageFile}
                                        pendingFile={pendingImageFiles[f.path]}
                                        onDelete={handleImageDelete}
                                        onRemovePath={handleImageRemovePath}
                                        disabled={!isEditing}
                                      />
                                    ))}
                                  </div>
                                )}
                                {visibleMediaFields.length > 0 &&
                                  !isEditing && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                      {visibleMediaFields.map((f) => (
                                        <div
                                          key={f.path}
                                          className="rounded-xl border border-slate-200/90 bg-white shadow-sm p-4"
                                        >
                                          <div className="text-sm font-semibold text-slate-700 mb-2">
                                            {f.label}
                                          </div>
                                          <div className="text-sm text-slate-600 break-words font-mono bg-slate-50 rounded-lg px-3 py-2 border border-slate-100">
                                            {formatFileFieldDisplay(
                                              getByPath(form || data, f.path),
                                            ) || "—"}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                              </>
                            );
                          })()}
                        </>
                      ),
                    },
                    {
                      key: "sys",
                      label: (
                        <span className="flex items-center gap-2">
                          <Settings size={16} />
                          System
                        </span>
                      ),
                      children: renderGrid(buildSysFieldsForPpc(), {
                        editing: false,
                        form: form || data,
                        onEnableEdit: null, // System fields are not editable
                      }),
                    },
                  ]}
                />
              </div>
            </div>
            {showPreview && (
              <>
                {/* Drag Handle */}
                <div
                  className="w-2 cursor-col-resize flex-shrink-0 transition-colors duration-200 relative group"
                  onMouseDown={() => setIsDragging(true)}
                >
                  <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute bg-gray-300 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1 h-6 rounded-full group-hover:opacity-100 transition-opacity duration-200" />
                </div>
                <div
                  className="flex-shrink-0"
                  style={{ width: `calc(${100 - previewWidth}% - 40px)` }}
                >
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4 w-full justify-between">
                        <div className="flex items-center text-sm text-gray-500">
                          <Zap size={16} className="mr-2" />
                          Real-time rendering
                        </div>
                        <Popover
                          content={
                            <div style={{ maxHeight: 600, overflow: "auto" }}>
                              <h4 className="font-semibold text-gray-900 mb-4 text-base border-b pb-2">
                                Markdown Formatting Guide
                              </h4>
                              <div className="space-y-4 text-sm">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <div className="font-medium text-gray-700 mb-2 text-xs uppercase tracking-wide">
                                      Syntax
                                    </div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      # Header 1
                                    </code>
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-700 mb-2 text-xs uppercase tracking-wide">
                                      Result
                                    </div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <h1 className="text-2xl font-bold">
                                        Header 1
                                      </h1>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      ## Header 2
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <h2 className="text-xl font-bold">
                                        Header 2
                                      </h2>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      ### Header 3
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <h3 className="text-lg font-bold">
                                        Header 3
                                      </h3>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      #### Header 4
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <h4 className="text-base font-bold">
                                        Header 4
                                      </h4>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      ##### Header 5
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <h5 className="text-sm font-bold">
                                        Header 5
                                      </h5>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      ###### Header 6
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <h6 className="text-xs font-bold">
                                        Header 6
                                      </h6>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      **bold text**
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <strong className="font-bold">
                                        bold text
                                      </strong>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      __bold text__
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <strong className="font-bold">
                                        bold text
                                      </strong>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      *italic text*
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <em className="italic">italic text</em>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      _italic text_
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <em className="italic">italic text</em>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      ***bold & italic***
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <strong className="font-bold italic">
                                        bold & italic
                                      </strong>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      - Item 1<br />
                                      - Item 2<br />- Item 3
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <ul className="list-disc list-inside">
                                        <li>Item 1</li>
                                        <li>Item 2</li>
                                        <li>Item 3</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      * Item 1<br />* Item 2
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <ul className="list-disc list-inside">
                                        <li>Item 1</li>
                                        <li>Item 2</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      + Item 1<br />+ Item 2
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <ul className="list-disc list-inside">
                                        <li>Item 1</li>
                                        <li>Item 2</li>
                                      </ul>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      1. First
                                      <br />
                                      2. Second
                                      <br />
                                      3. Third
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <ol className="list-decimal list-inside">
                                        <li>First</li>
                                        <li>Second</li>
                                        <li>Third</li>
                                      </ol>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      [Link text](https://example.com)
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <a
                                        href="#"
                                        className="text-blue-600 underline"
                                      >
                                        Link text
                                      </a>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      [Link with title](https://example.com
                                      "Title")
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <a
                                        href="#"
                                        title="Title"
                                        className="text-blue-600 underline"
                                      >
                                        Link with title
                                      </a>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      ![Alt text](image.jpg)
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded text-xs text-gray-600">
                                      Image with alt text
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      `inline code`
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <code className="bg-gray-200 px-1 rounded font-mono text-xs">
                                        inline code
                                      </code>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      ```
                                      <br />
                                      code block
                                      <br />
                                      multiple lines
                                      <br />
                                      ```
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <pre className="bg-gray-200 p-2 rounded font-mono text-xs">
                                        code block
                                        <br />
                                        multiple lines
                                      </pre>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      &gt; Blockquote
                                      <br />
                                      &gt; Second line
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <blockquote className="border-l-4 border-gray-400 pl-3 italic text-gray-700">
                                        Blockquote
                                        <br />
                                        Second line
                                      </blockquote>
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <code className="block bg-gray-50 p-2 rounded text-xs font-mono">
                                      ---
                                    </code>
                                  </div>
                                  <div>
                                    <div className="bg-blue-50 p-2 rounded">
                                      <hr className="border-t border-gray-400" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          }
                          title={null}
                          trigger="hover"
                          placement="bottomLeft"
                          overlayStyle={{ width: 600 }}
                        >
                          <Button
                            type="link"
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 transition-colors"
                          >
                            <HelpCircle size={16} />
                            <span>Markdown Guide</span>
                          </Button>
                        </Popover>
                      </div>
                    </div>
                    {subTypes && (
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-sm">Preview As:</span>
                        <div className="rounded-lg">
                          <Select
                            className="w-48"
                            options={subTypes}
                            value={ppcType}
                            defaultValue={subTypes[0].value}
                            onChange={(value) => setPpcType(value)}
                          />
                        </div>
                      </div>
                    )}
                    <div className="rounded-lg">
                      <PreviewOrchestrator
                        objectType={ppcType}
                        id={data?.id}
                        payload={payload}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
